// The waiver engine (WAIVERS.md "Processing: the engine"): every pending
// claim in, every claim decided out, each with a sentence a person would
// accept as fair. Deterministic; no clocks, no I/O, no sport names.
//
// Order of operations:
//   1. Players resolve in the order their claim periods ended, ties by the
//      earliest claim on them.
//   2. Within a player, claims rank by bid (FAAB) or by waiver priority.
//      Before a team's claim is tried, that team's earlier-ordered claims on
//      other players are decided first, so "try this claim 2nd of 3" holds.
//      A conditional claim is skipped when its parent won, in this run or
//      an earlier one (`earlier`).
//   3. The top claim that is still valid wins; every other claim on the
//      player gets exactly one outcome.
//   4. The award changes the roster, the budget, and (rolling, or a budget
//      league's tiebreak order) the priority.

import { rosterCapacity, type WaiverPrioritySource } from '@fsp/domain';
import { activeOf, fitProblem } from './fit';
import type {
  AcquisitionCount,
  AcquisitionLimits,
  Claim,
  ClaimFacts,
  ClaimResult,
  StandingRow,
  Transaction,
  WaiverContext,
  WaiverRules,
} from './types';

export interface ResolveInput {
  rules: WaiverRules;
  limits: AcquisitionLimits;
  /** Every pending claim from every team. */
  claims: readonly Claim[];
  rosters: Readonly<Record<string, readonly string[]>>;
  /** FAAB remaining per team; ignored in priority systems. */
  budgets: Readonly<Record<string, number>>;
  /**
   * The order, best first: a rolling league's, or a budget league's tiebreak
   * order. Reverse-standings ignores it and reads standings.
   */
  priority: readonly string[];
  standings: readonly StandingRow[];
  /**
   * Players the game-time lock holds at the run's instant: game started,
   * period not over. Neither a claimed player nor a claim's drop may be one.
   */
  locked: ReadonlySet<string>;
  acquisitionsUsed: Readonly<Record<string, AcquisitionCount>>;
  /** Per player, when its claim period ended; missing means "now". */
  clearsAt: Readonly<Record<string, number>>;
  now: number;
  runId: string;
  /**
   * Claims an earlier run won that a claim here is conditional on, by id: a
   * player who clears at his own time (a continuous schedule, or two runs a
   * week) is decided at his own run, and his claim's child at another
   * (B-523). Absent, only this run's claims are parents.
   */
  earlier?: Readonly<Record<string, { playerId: string }>>;
}

export interface ResolveOutput {
  /** One per claim, in the order they were decided. */
  results: ClaimResult[];
  rosters: Record<string, string[]>;
  budgets: Record<string, number>;
  priority: string[];
  acquisitionsUsed: Record<string, AcquisitionCount>;
  /** What actually changed: one per award. */
  transactions: Transaction[];
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

function recordLabel(row: StandingRow | undefined): string {
  if (!row) return '';
  return row.ties ? `${row.wins}–${row.losses}–${row.ties}` : `${row.wins}–${row.losses}`;
}

/** Worst record first, ties by fewer points for: the reverse-standings order. */
export function reverseStandingsOrder(standings: readonly StandingRow[]): string[] {
  return [...standings]
    .sort((a, b) => {
      const pa = a.wins + a.ties / 2;
      const pb = b.wins + b.ties / 2;
      return pa - pb || a.pointsFor - b.pointsFor;
    })
    .map((r) => r.teamId);
}

/**
 * Where a league's waiver priority comes from right now — the fact
 * `WaiversView.priority.source` carries beside its sentence. Null when no
 * order decides anything: a league with no waivers, and a budget league whose
 * tied bids go to the earlier claim — its view said "It only matters on a tied
 * bid" about an order that never mattered at all.
 *
 * The order is the standings' when the system is reverse standings, or a
 * budget league breaks a tied bid on them; then it is the reverse of the draft
 * while every record is 0–0, and frozen once the playoffs have started, when
 * the table stops moving (PLAN-2026-09-24 §2 rule 2). It is `rolling` exactly
 * when `orderRolls` says a win moves a team back. One function for every host,
 * so the simulator and the site cannot name the same order two ways.
 */
export function prioritySource(
  rules: WaiverRules,
  standings: readonly StandingRow[],
  playoffsStarted: boolean,
): WaiverPrioritySource | null {
  if (rules.system === 'none') return null;
  if (orderRolls(rules)) return 'rolling';
  const byStandings =
    rules.system === 'reverse-standings' ||
    (rules.system === 'faab' && rules.faab?.tiebreak === 'reverse-standings');
  if (!byStandings) return null;
  if (playoffsStarted) return 'frozen';
  // NOBODY HAS PLAYED: no record and no points. A total-points table has
  // no record at all (Q5, B-361), and its order is its points from week 1.
  return standings.every((r) => r.wins + r.losses + r.ties === 0 && r.pointsFor === 0)
    ? 'draft-order'
    : 'standings';
}

/**
 * Whether a won claim sends its team to the back of the order: a rolling
 * league's order, and the order a budget league's tied bids go by when its
 * tiebreak is waiver priority — every win moves that team back, whatever
 * decided it, as on Sleeper, ESPN and Yahoo (PLAN-2026-09-24 §2 rule 2). The
 * run rotates on it and `prioritySource` calls that order `rolling` on it, so
 * the label on the view is the behavior of the run.
 */
export function orderRolls(rules: WaiverRules): boolean {
  if (rules.system === 'rolling') return true;
  return (
    rules.system === 'faab' && (rules.faab?.tiebreak ?? 'waiver-priority') === 'waiver-priority'
  );
}

/** "A", "A and B", "A, B and C". */
function listOf(items: readonly string[]): string {
  if (items.length <= 2) return items.join(' and ');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

export function emptyCount(): AcquisitionCount {
  return { period: 0, season: 0, byPosition: {} };
}

/** Which limit a pickup at `pos` would break for this team, or null. */
export function limitHit(
  limits: AcquisitionLimits,
  used: AcquisitionCount | undefined,
  pos: string,
): ClaimFacts['limit'] {
  const u = used ?? emptyCount();
  if (limits.perPeriod !== null && u.period >= limits.perPeriod)
    return { kind: 'period', value: limits.perPeriod };
  if (limits.perSeason !== null && u.season >= limits.perSeason)
    return { kind: 'season', value: limits.perSeason };
  const cap = limits.perPosition[pos];
  if (cap !== undefined && (u.byPosition[pos] ?? 0) >= cap)
    return { kind: 'position', value: cap, position: pos };
  return null;
}

export function limitSentence(limit: NonNullable<ClaimFacts['limit']>): string {
  const n = `${limit.value} pickup${limit.value === 1 ? '' : 's'}`;
  if (limit.kind === 'period') return `all ${n} this week`;
  if (limit.kind === 'season') return `all ${n} this season`;
  return `all ${n} at ${limit.position}`;
}

export function resolveWaivers(input: ResolveInput, ctx: WaiverContext): ResolveOutput {
  const { rules, limits, now } = input;
  const rosters: Record<string, string[]> = Object.fromEntries(
    Object.entries(input.rosters).map(([t, r]) => [t, [...r]]),
  );
  const budgets: Record<string, number> = { ...input.budgets };
  const used: Record<string, AcquisitionCount> = Object.fromEntries(
    Object.entries(input.acquisitionsUsed).map(([t, u]) => [
      t,
      { ...u, byPosition: { ...u.byPosition } },
    ]),
  );
  const rolling = [...input.priority];
  const priority =
    rules.system === 'reverse-standings' ? reverseStandingsOrder(input.standings) : rolling;
  const standingsRank = new Map(reverseStandingsOrder(input.standings).map((t, i) => [t, i + 1]));
  const rankIn = (order: readonly string[], teamId: string) => {
    const i = order.indexOf(teamId);
    return i === -1 ? order.length + 1 : i + 1;
  };
  const rank = (teamId: string) => rankIn(priority, teamId);
  /** Priority as it stood when the player being decided came up; sentences quote this. */
  let snapshot: readonly string[] = priority;
  const rankThen = (teamId: string) => rankIn(snapshot, teamId);
  const faab = rules.system === 'faab';
  const tiebreak = rules.faab?.tiebreak ?? 'waiver-priority';
  const rolls = orderRolls(rules);
  const standingOf = (teamId: string) => input.standings.find((s) => s.teamId === teamId);

  const pending = input.claims.filter((c) => c.status === 'pending');
  const byId = new Map(pending.map((c) => [c.id, c]));
  const results: ClaimResult[] = [];
  const decided = new Map<string, ClaimResult>();
  const transactions: Transaction[] = [];
  const done = new Set<string>();
  const resolving = new Set<string>();
  /** Per team, the players it has won so far in this run: what an over-budget sentence names. */
  const wonBy = new Map<string, string[]>();

  const owned = (playerId: string) =>
    Object.values(rosters).some((roster) => roster.includes(playerId));

  const compare = (a: Claim, b: Claim): number => {
    if (rules.system === 'none') return a.placedAt - b.placedAt;
    if (faab) {
      const bidDiff = (b.bid ?? 0) - (a.bid ?? 0);
      if (bidDiff !== 0) return bidDiff;
      if (tiebreak === 'earlier-claim') return a.placedAt - b.placedAt;
      if (tiebreak === 'reverse-standings')
        return (standingsRank.get(a.teamId) ?? 99) - (standingsRank.get(b.teamId) ?? 99);
      return rank(a.teamId) - rank(b.teamId);
    }
    return rank(a.teamId) - rank(b.teamId) || a.order - b.order;
  };

  const record = (
    claim: Claim,
    outcome: ClaimResult['outcome'],
    sentence: string,
    facts: Partial<ClaimFacts>,
  ) => {
    const result: ClaimResult = {
      claimId: claim.id,
      teamId: claim.teamId,
      playerId: claim.playerId,
      outcome,
      sentence,
      facts: {
        teamId: claim.teamId,
        playerId: claim.playerId,
        bid: claim.bid,
        dropPlayerIds: [...claim.dropPlayerIds],
        winningTeamId: null,
        winningBid: null,
        priorityRank: null,
        winnerRank: null,
        budgetLeft: faab ? (budgets[claim.teamId] ?? 0) : null,
        limit: null,
        problem: null,
        parentPlayerId: null,
        ...facts,
      },
    };
    results.push(result);
    decided.set(claim.id, result);
    return result;
  };

  const team = ctx.teamName;
  const player = ctx.playerName;
  const money = (n: number | null) => `$${n ?? 0}`;

  const lostTo = (claim: Claim, winner: Claim) => {
    const w = team(winner.teamId);
    const p = player(claim.playerId);
    const base = {
      winningTeamId: winner.teamId,
      winningBid: winner.bid,
      priorityRank: rankThen(claim.teamId),
      winnerRank: rankThen(winner.teamId),
    };
    if (rules.system === 'none') {
      record(claim, 'lost-priority', `${w} claimed ${p} first.`, base);
      return;
    }
    if (faab) {
      if ((claim.bid ?? 0) < (winner.bid ?? 0)) {
        record(claim, 'lost-bid', `Outbid: ${w} paid ${money(winner.bid)} for ${p}.`, base);
        return;
      }
      const why =
        tiebreak === 'earlier-claim'
          ? `${w} claimed first`
          : tiebreak === 'reverse-standings'
            ? `${w} has the worse record (${recordLabel(standingOf(winner.teamId))})`
            : `${w} was ahead on waiver priority`;
      record(
        claim,
        'lost-tiebreak',
        `Same bid as ${w}, ${money(winner.bid)}, for ${p}; ${why}.`,
        base,
      );
      return;
    }
    const wr = rankThen(winner.teamId);
    const cr = rankThen(claim.teamId);
    const why =
      rules.system === 'reverse-standings'
        ? `${w} had ${ordinal(wr)} priority at ${recordLabel(standingOf(winner.teamId))}; ${team(claim.teamId)} had ${ordinal(cr)} at ${recordLabel(standingOf(claim.teamId))}.`
        : `${w} had ${ordinal(wr)} priority; ${team(claim.teamId)} had ${ordinal(cr)}.`;
    record(claim, 'lost-priority', `Beaten to ${p}. ${why}`, base);
  };

  /** Decide every claim of this team with a lower order first, so the team's own order holds. */
  const settleEarlier = (claim: Claim) => {
    for (const other of pending) {
      if (other.teamId !== claim.teamId || other.id === claim.id) continue;
      if (other.order < claim.order && !decided.has(other.id) && !done.has(other.playerId))
        resolvePlayer(other.playerId);
    }
  };

  const resolvePlayer = (playerId: string) => {
    if (done.has(playerId) || resolving.has(playerId)) return;
    resolving.add(playerId);
    const claims = pending.filter((c) => c.playerId === playerId).sort(compare);
    for (const c of claims) {
      const parent = c.conditionalOnClaimId ? byId.get(c.conditionalOnClaimId) : null;
      if (parent && !decided.has(parent.id) && !done.has(parent.playerId))
        resolvePlayer(parent.playerId);
    }
    let winner: Claim | null = null;
    let winnerSnapshot: readonly string[] = priority;
    // SORTED AGAIN WHENEVER SETTLING DECIDED SOMETHING (the audit of
    // 2026-10-20). Settling a team's earlier claims can win one and send the
    // team to the back of a rolling order, so an order sorted before it is
    // stale: the claim tried next is the first of a fresh sort, and a team
    // that rolled behind another loses this player to it (WAIVERS.md step 4).
    // Until then the first sort stood, and a team won two players over a
    // team that was ahead of it by the time the second was decided.
    for (;;) {
      const open = claims.filter((x) => !decided.has(x.id));
      if (open.length === 0) break;
      const c = open.sort(compare)[0]!;
      if (winner) {
        snapshot = winnerSnapshot;
        lostTo(c, winner);
        continue;
      }
      // Earlier claims of other teams may have shifted priority; quote the order as it stands now.
      snapshot = [...priority];
      const p = player(c.playerId);
      const t = team(c.teamId);
      if (c.conditionalOnClaimId) {
        const parent =
          decided.get(c.conditionalOnClaimId) ??
          (input.earlier?.[c.conditionalOnClaimId] === undefined
            ? undefined
            : { outcome: 'won' as const, ...input.earlier[c.conditionalOnClaimId]! });
        if (parent?.outcome === 'won') {
          record(
            c,
            'skipped-conditional',
            `The claim ${t} placed for ${player(parent.playerId)} won, so this one was not tried.`,
            { parentPlayerId: parent.playerId },
          );
          continue;
        }
      }
      const settled = decided.size;
      settleEarlier(c);
      if (decided.has(c.id) || decided.size !== settled) continue;
      if (owned(c.playerId)) {
        record(c, 'gone', `Someone added ${p} before this claim processed.`, {});
        continue;
      }
      if (rules.gameTimeLock && input.locked.has(c.playerId)) {
        record(c, 'locked', `The game ${p} plays in had already started.`, {});
        continue;
      }
      const pos = ctx.positionOf(c.playerId);
      const limit = limitHit(limits, used[c.teamId], pos);
      if (limit) {
        record(c, 'limit-reached', `${t} had used ${limitSentence(limit)}.`, { limit });
        continue;
      }
      if (faab && (c.bid ?? 0) > (budgets[c.teamId] ?? 0)) {
        // THE AMOUNT LEFT, AND AN EARLIER WIN ONLY WHEN THERE WAS ONE. A bid is
        // checked against the whole budget when it is placed (rule 5), so it
        // is over here because the budget dropped since: most often a win
        // earlier in this run, which the sentence names; otherwise something
        // outside it (a commissioner's edit, a budget traded away), which it
        // does not pretend to know. Until W3 it always said "after an earlier
        // claim won", whether or not one had.
        const earlier = wonBy.get(c.teamId) ?? [];
        const after = earlier.length ? ` after winning ${listOf(earlier.map(player))}` : '';
        record(
          c,
          'over-budget',
          `${t} bid ${money(c.bid)} with ${money(budgets[c.teamId] ?? 0)} left${after}.`,
          {},
        );
        continue;
      }
      const roster = rosters[c.teamId] ?? [];
      const goneDrop = c.dropPlayerIds.find((id) => !roster.includes(id));
      if (goneDrop) {
        record(c, 'roster-full', `The drop, ${player(goneDrop)}, was already gone.`, {
          problem: 'drop-gone',
        });
        continue;
      }
      // A DROP WHOSE GAME HAS STARTED, AT A RUN INSIDE THE PERIOD. The lock
      // keeps a started player on his roster until the period ends (rule 6),
      // and a claim's drop happens here, at the run. A weekly run comes after
      // the period, when nobody is locked; a daily one can come during it. The
      // placement check refuses a drop locked when the claim is placed
      // (`dropLockHolds`); this catches one whose game started since. Until
      // the W3 review the run never looked, and a daily run dropped a started
      // player.
      const lockedDrop = rules.gameTimeLock
        ? c.dropPlayerIds.find((id) => input.locked.has(id))
        : undefined;
      if (lockedDrop) {
        record(
          c,
          'locked',
          `The drop, ${player(lockedDrop)}, was locked because the game had already started.`,
          { problem: 'drop-locked' },
        );
        continue;
      }
      const after = [...roster.filter((id) => !c.dropPlayerIds.includes(id)), c.playerId];
      // Nobody on IR who has to come off (D9), by count less IR, then by the
      // host's lineup: a roster that counts right can still leave the new man
      // in no cell (`WaiverContext.fits`).
      const problem = fitProblem(c.teamId, after, ctx);
      if (problem) {
        const sentence =
          c.dropPlayerIds.length === 0 &&
          activeOf(c.teamId, after, ctx).length > rosterCapacity(ctx.roster)
            ? `${t} had no room for ${p} and no drop chosen.`
            : `${t} could not fit ${p}. ${problem}`;
        record(c, 'roster-full', sentence, { problem });
        continue;
      }
      // Award.
      winner = c;
      winnerSnapshot = snapshot;
      rosters[c.teamId] = after;
      if (faab) budgets[c.teamId] = (budgets[c.teamId] ?? 0) - (c.bid ?? 0);
      wonBy.set(c.teamId, [...(wonBy.get(c.teamId) ?? []), c.playerId]);
      const u = used[c.teamId] ?? emptyCount();
      used[c.teamId] = {
        period: u.period + 1,
        season: u.season + 1,
        byPosition: { ...u.byPosition, [pos]: (u.byPosition[pos] ?? 0) + 1 },
      };
      const drops = c.dropPlayerIds.map(player).join(', ');
      const forMoney = faab ? ` for ${money(c.bid)}` : '';
      record(c, 'won', `${t} got ${p}${forMoney}.${drops ? ` Dropped ${drops}.` : ''}`, {
        winningTeamId: c.teamId,
        winningBid: c.bid,
        priorityRank: rankThen(c.teamId),
        winnerRank: rankThen(c.teamId),
        budgetLeft: faab ? budgets[c.teamId]! : null,
      });
      if (rolls) {
        const i = rolling.indexOf(c.teamId);
        if (i !== -1) rolling.splice(i, 1);
        rolling.push(c.teamId);
      }
      transactions.push({
        id: `${input.runId}:${c.id}`,
        at: now,
        kind: 'claim',
        teamId: c.teamId,
        added: [c.playerId],
        dropped: [...c.dropPlayerIds],
        faab: faab ? c.bid : null,
        reason: null,
        reversedBy: null,
        claimId: c.id,
        runId: input.runId,
      });
    }
    resolving.delete(playerId);
    done.add(playerId);
  };

  const firstClaimAt = (playerId: string) =>
    Math.min(...pending.filter((c) => c.playerId === playerId).map((c) => c.placedAt));
  const players = [...new Set(pending.map((c) => c.playerId))].sort((a, b) => {
    const ca = input.clearsAt[a] ?? now;
    const cb = input.clearsAt[b] ?? now;
    return ca - cb || firstClaimAt(a) - firstClaimAt(b);
  });
  for (const p of players) resolvePlayer(p);

  return {
    results,
    rosters,
    budgets,
    priority: rules.system === 'reverse-standings' ? priority : rolling,
    acquisitionsUsed: used,
    transactions,
  };
}
